import React, { memo } from "react"
import { Handle, Position, NodeProps } from "@xyflow/react"
import { ActionNodeData } from "lib/types/workflow"

import { NodeContainer, NodeHeader, NodeBody, ActionInfo } from "./ActionNode.styled"
import {
  StatusBadge,
  FailedMessageBlock,
  statusIcon
} from "./ExecutionOverlay.styled"
import TerminalAddButton from "./TerminalAddButton"
import { HANDLE_INPUT, HANDLE_OUTPUT } from "../../nodeKind"

type Props = NodeProps & { id: string; data: ActionNodeData }

const ActionNode = memo(({ id, data }: Props) => {
  const getActionLabel = () => {
    if (data.actionType === "CANCELLATION") {
      return `Cancel${data.reason ? ` (${data.reason})` : ""}`
    }
    if (data.actionType === "DELIVERY") {
      const parts: string[] = []
      if (data.deliveryGiftCardProducts) parts.push("gift card products")
      return parts.length > 0 ? `Deliver ${parts.join(", ")}` : "Deliver (no selection)"
    }
    if (data.actionType === "CUSTOM_STATE") {
      return `Set state: ${data.customState ?? "—"}`
    }
    return data.actionType
  }

  const actionLabel = getActionLabel()

  const showAddButton =
    (data._terminalHandles ?? []).includes(HANDLE_OUTPUT) && !!data._onAddAtTerminal
  const status = data._executionStatus

  return (
    <NodeContainer $skipped={status === "skipped"}>
      <Handle type="target" position={Position.Top} id={HANDLE_INPUT} />
      <NodeHeader>Action</NodeHeader>
      <NodeBody>{data.label}</NodeBody>
      <ActionInfo>{actionLabel}</ActionInfo>
      {data._failedMessage && (
        <FailedMessageBlock>{data._failedMessage}</FailedMessageBlock>
      )}
      <Handle type="source" position={Position.Bottom} id={HANDLE_OUTPUT} />
      {showAddButton && (
        <TerminalAddButton
          onClick={() => data._onAddAtTerminal?.(id, HANDLE_OUTPUT)}
          highlighted={data._activeTerminalDropHandle === HANDLE_OUTPUT}
        />
      )}
      {status && (
        <StatusBadge $status={status}>{statusIcon(status)}</StatusBadge>
      )}
    </NodeContainer>
  )
})

ActionNode.displayName = "ActionNode"

export default ActionNode
